import { useQueryClient } from '@tanstack/react-query';
import { addDays, format, parseISO, subDays } from 'date-fns';
import { useRef, useState } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/dist/style.css';
import { getDiaryData } from '../../api/diary-service';
import { svExtended } from '../../config/custom-locales';
import { formatDateDisplay } from '../../helpers';
import useMealDatesForMonth from '../../hooks/data/meals/useMealDatesForMonth';
import useDateFromUrl from '../../hooks/utils/useDateFromUrl';
import useHandleClickOutside from '../../hooks/utils/useHandleClickOutside';
import ErrorMessage from '../ui/ErrorMessage';
import LoadingSpinner from '../ui/LoadingSpinner';

const DiaryDatePicker = ({ current_day }) => {
    const queryClient = useQueryClient();

    const currentDay = useDateFromUrl(current_day);

    const [calendarOpen, setCalendarOpen] = useState(false);

    const dayPickerRef = useRef(null);
    const buttonRef = useRef(null);

    const { mealDates, isLoading, isError, error, visibleMonth, setVisibleMonth } =
        useMealDatesForMonth(currentDay);

    const formatDate = (day) => {
        const yyyy = day.getFullYear();
        const mm = String(day.getMonth() + 1).padStart(2, '0');
        const dd = String(day.getDate()).padStart(2, '0');
        return `${yyyy}-${mm}-${dd}`;
    };

    const goToDate = (dateStr) => {
        queryClient.prefetchQuery({
            queryKey: ['diary', dateStr],
            queryFn: () => getDiaryData(dateStr)
        });

        const url = new URL(window.location.href);
        url.searchParams.set('date', dateStr);
        window.history.pushState({}, '', url);

        window.dispatchEvent(new Event('diary-date-change'));

        setVisibleMonth(parseISO(dateStr));
    };

    useHandleClickOutside([dayPickerRef, buttonRef], calendarOpen, setCalendarOpen);

    // DERIVED/UI
    const activeDateObj = parseISO(currentDay);

    const dateDisplay = formatDateDisplay(currentDay);
    const previousDay = format(subDays(activeDateObj, 1), 'yyyy-MM-dd');
    const nextDay = format(addDays(activeDateObj, 1), 'yyyy-MM-dd');

    return (
        <div className="diary-date-picker">
            <ul className="current-month">
                <li>
                    <button
                        className="back-forth-btn"
                        type="button"
                        onClick={() => goToDate(previousDay)}
                        aria-label="Previous day"
                    >
                        <img src="/assets/images/diary-icons/chevron-left.svg" alt="Previous day" />
                    </button>
                </li>
                <li>
                    <button type="button" ref={buttonRef} onClick={() => setCalendarOpen(!calendarOpen)}>
                        <img src="/assets/images/diary-icons/calendar.svg" alt="" />
                        {dateDisplay}
                    </button>

                    {calendarOpen && (
                        <div className="diary-day-picker" ref={dayPickerRef}>
                            {isLoading ? (
                                <LoadingSpinner height={290} />
                            ) : isError ? (
                                <ErrorMessage message={error?.message} />
                            ) : (
                                <DayPicker
                                    mode="single"
                                    selected={parseISO(currentDay)}
                                    locale={svExtended}
                                    month={visibleMonth}
                                    onMonthChange={setVisibleMonth}
                                    onSelect={(day) => {
                                        if (!day) return;
                                        goToDate(formatDate(day));
                                    }}
                                    modifiers={{
                                        hasMeal: mealDates.map((date) => parseISO(date))
                                    }}
                                    modifiersClassNames={{
                                        hasMeal: 'highlighted-day'
                                    }}
                                />
                            )}
                        </div>
                    )}
                </li>
                <li>
                    <button
                        className="back-forth-btn"
                        type="button"
                        onClick={() => goToDate(nextDay)}
                        aria-label="Next day"
                    >
                        <img src="/assets/images/diary-icons/chevron-right.svg" alt="Next day" />
                    </button>
                </li>
            </ul>
        </div>
    );
};

export default DiaryDatePicker;
